import { z } from "zod";
import { queryOptions, UseQueryOptions } from "@tanstack/react-query";

import { api, mBaseURL } from "@/config/api";
import { unwrapApiError } from "@/lib/error";
import { createQueryString, createURLPath } from "@/lib/utils";

import { APIResponseSuccessSchema } from "@/schema/api";
import { ChatInviteSchema, ChatMessage, ChatSession, ChatSessionSchema, ChatVisibility } from "@/schema/chat";
import { SegmentedImageEditor } from "@/schema/viewport";

type QueryOptions<T> = Omit<UseQueryOptions<T>, "queryKey" | "queryFn">;

export interface ChatListResponse {
  items: ChatSession[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface FetchChatListParams extends Record<string, string | number | boolean | null | undefined> {
  page: number;
  size: number;
}

export interface CreateChatSessionProps {
  agent: string;
  settings: Record<string, string>;
}

export interface CreateChatMessageProps {
  session: string;
  message: ChatMessage[];
  settings: Record<string, string>;
}

export interface UpdateChatEditorItemProps {
  editor_item_id: string;
  editor_item_current: SegmentedImageEditor;
}

export const ChatApiFactory = {
  Endpoint: mBaseURL + "/api/chat",
  Keys: {
    FetchInfiniteChatSessions: (params: Omit<FetchChatListParams, "page" | "size">) => ["chat", "infinite", createQueryString(params)],
    FetchChatSession: (session: string) => ["chat", session],
    FetchChatSessionPublic: (session: string) => ["chat", session, "public"],
    FetchChatSessionRestricted: (session: string) => ["chat", session, "restricted"],
    FetchChatSessions: (params: FetchChatListParams) => ["chat", createQueryString(params)],
  },
  Schemas: {
    Response: {
      ChatSessions: APIResponseSuccessSchema.extend({
        data: z.object({
          items: z.array(ChatSessionSchema),
          total: z.number(),
          page: z.number(),
          size: z.number(),
          pages: z.number(),
        }),
      }),
      ChatSession: APIResponseSuccessSchema.extend({
        data: ChatSessionSchema,
      }),
      ShareChat: APIResponseSuccessSchema.extend({
        data: ChatInviteSchema,
      }),
    },
  },
  Apis: {
    CreateChatSession: async ({ agent, ...body }: CreateChatSessionProps) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint), {
        method: "POST",
        body: JSON.stringify({ agent_name: agent, ...body }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ChatApiFactory.Schemas.Response.ChatSession.parse)
        .then((response) => response.data);
    },
    CreateChatMessage: async ({ session, ...body }: CreateChatMessageProps) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, session, "message"), {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json();
    },
    FetchChatSession: async (id: string) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, id), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ChatApiFactory.Schemas.Response.ChatSession.parse)
        .then((response) => response.data);
    },
    FetchChatSessionPublic: async (id: string) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, "p", id), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ChatApiFactory.Schemas.Response.ChatSession.parse)
        .then((response) => response.data);
    },
    FetchChatSessionRestricted: async (id: string) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, "r", id), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ChatApiFactory.Schemas.Response.ChatSession.parse)
        .then((response) => response.data);
    },
    FetchChatSessions: async (params: FetchChatListParams) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint) + "?" + createQueryString(params), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ChatApiFactory.Schemas.Response.ChatSessions.parse)
        .then((response) => response.data);
    },
    RenameChatSession: async (id: string, title: string) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, id, "name"), {
        method: "PATCH",
        body: JSON.stringify({ title }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json();
    },
    UpdateChatRatings: async (id: string, ratings: Record<string, number>) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, id, "ratings"), {
        method: "PATCH",
        body: JSON.stringify({ ratings }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json();
    },
    UpdateChatSessionID: async (id: string, stream_id: string | null) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, id, "stream"), {
        method: "PATCH",
        body: JSON.stringify({ stream_id }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json();
    },
    UpdateChatNotificationPreference: async (id: string, notify: boolean) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, id, "notification"), {
        method: "PATCH",
        body: JSON.stringify({ notify }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json();
    },
    UpdateChatChoices: async (id: string, choices: string[]) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, id, "choices"), {
        method: "PATCH",
        body: JSON.stringify({ choices }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json();
    },
    UpdateChatEditorItem: async (chat_id: string, body: UpdateChatEditorItemProps) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, chat_id, "editor", "item"), {
        method: "PATCH",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }
    },
    DeleteChatSession: async (id: string) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, id), {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json();
    },
    InviteUserToChat: async (chat_id: string, email: string) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, chat_id, "invite"), {
        method: "POST",
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ChatApiFactory.Schemas.Response.ShareChat.parse)
        .then((response) => response.data);
    },
    UninviteUserFromChat: async (chat_id: string, email: string) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, chat_id, "uninvite"), {
        method: "POST",
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ChatApiFactory.Schemas.Response.ShareChat.parse)
        .then((response) => response.data);
    },
    UpdateChatVisibility: async (chat_id: string, visibility: ChatVisibility) => {
      const response = await api(createURLPath(ChatApiFactory.Endpoint, chat_id, "visibility"), {
        method: "POST",
        body: JSON.stringify({ visibility }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json();
    },
  },
  Queries: {
    FetchChatSession: (session: string, options?: QueryOptions<ChatSession>) => {
      return queryOptions({
        queryKey: ChatApiFactory.Keys.FetchChatSession(session),
        queryFn: () => ChatApiFactory.Apis.FetchChatSession(session),
        ...options,
      });
    },
    FetchChatSessionPublic: (session: string, options?: QueryOptions<ChatSession>) => {
      return queryOptions({
        queryKey: ChatApiFactory.Keys.FetchChatSessionPublic(session),
        queryFn: () => ChatApiFactory.Apis.FetchChatSessionPublic(session),
        ...options,
      });
    },
    FetchChatSessionRestricted: (session: string, options?: QueryOptions<ChatSession>) => {
      return queryOptions({
        queryKey: ChatApiFactory.Keys.FetchChatSessionRestricted(session),
        queryFn: () => ChatApiFactory.Apis.FetchChatSessionRestricted(session),
        ...options,
      });
    },
    FetchChatSessions: (params: FetchChatListParams, options?: QueryOptions<ChatListResponse>) => {
      return queryOptions({
        queryKey: ChatApiFactory.Keys.FetchChatSessions(params),
        queryFn: () => ChatApiFactory.Apis.FetchChatSessions(params),
        ...options,
      });
    },
  },
};
